import React from 'react';
import { flexRender } from '@tanstack/react-table';
import type { Cell } from '@tanstack/table-core';

import { detectColumnType } from './column-type-icon';
import { DateTimeCell } from './date-time-cell';
import { BooleanCell } from './boolean-cell';
import { CopyableCell } from './copyable-cell';

interface CellRendererProps<TData = unknown> {
  value: unknown;
  columnId: string;
  rowId: string;
  enableCellCopy: boolean;
  onCellCopy: (value: string, columnId?: string, rowId?: string) => void;
  context: ReturnType<Cell<TData, unknown>['getContext']>;
  cell: Cell<TData, unknown>;
}

// Enhanced date detection
export const isDateTime = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;

  const date = new Date(value as Date | string | number);
  return !isNaN(date.getTime());
};

// Check if value appears to be a time-only value
export const isTimeOnly = (value: unknown): boolean => {
  if (typeof value !== 'string') return false;

  // Check if string matches time formats like "14:30", "02:30 PM", etc.
  const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9](?:\s*[AP]M)?$/i;
  return timeRegex.test(value);
};

// Check if value appears to be date-only
export const isDateOnly = (value: unknown): boolean => {
  if (typeof value !== 'string') return false;

  // Check if string matches date formats like "2024-01-15", "01/15/2024", etc.
  const dateRegex = /^\d{4}[-/]\d{2}[-/]\d{2}$|^\d{2}[/]\d{2}[/]\d{4}$/;
  return dateRegex.test(value) && !isTimeOnly(value);
};

export const SmartCellRenderer = <TData,>({
  value,
  columnId,
  rowId,
  enableCellCopy,
  onCellCopy,
  context,
  cell,
}: CellRendererProps<TData>) => {
  // Detect column type
  const columnType = detectColumnType(value);

  // Render the base cell content
  const renderBaseContent = () => {
    // Handle datetime cells with enhanced formatting
    if (columnType === 'datetime' || columnType === 'date') {
      const showTime = columnType === 'datetime' || isTimeOnly(value);
      const showDate = columnType === 'date' || isDateOnly(value) || !isTimeOnly(value);

      return (
        <DateTimeCell
          value={value}
          showTime={showTime}
          showDate={showDate}
          dateFormat={{
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          }}
          timeFormat={{
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }}
        >
          {flexRender(cell.column.columnDef.cell, context)}
        </DateTimeCell>
      );
    }

    // Handle boolean cells with colored badges
    if (columnType === 'boolean') {
      return (
        <BooleanCell
          value={value}
          trueLabel="Yes"
          falseLabel="No"
          trueColor="green"
          falseColor="red"
          variant="subtle"
        >
          {flexRender(cell.column.columnDef.cell, context)}
        </BooleanCell>
      );
    }

    // For other types, use the original cell rendering
    return flexRender(cell.column.columnDef.cell, context);
  };

  // Wrap with CopyableCell if enabled
  if (enableCellCopy) {
    return (
      <CopyableCell
        value={value}
        enableCopy={enableCellCopy}
        columnId={columnId}
        rowId={rowId}
        onCopy={onCellCopy}
      >
        {renderBaseContent()}
      </CopyableCell>
    );
  }

  // Return base content without copy functionality
  return renderBaseContent();
};

export { detectColumnType };